import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  ExperimentProcedure,
  selectFlowchartVersion,
  useExperimentProcedure,
} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import ProcedureDropdown, {
  ProcedureValidationLoadingSpinner,
} from 'studies/typed/shared/procedure-runs/procedure-selection/ProcedureDropdown';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

export interface ProcedureChangeParams {
  procedureId: string;
  procedure: ExperimentProcedure;
  templateProcedureFlowchartVersionId?: string;
  workflowFlowchartConfigVersionId?: string;
}

interface BaseEditProcedureModalProps {
  procedureId: string;
  flowchartVersionId: string;
  procedureType: ProcedureType;
  studyType: StudyType;
  onRequestClose: () => void;
  // Only used in PROCEDURE_CHANGE mode (redeclared as required there). Declared here
  // because legacyRunModal's type signature drops fields that aren't shared across all
  // variants of the union, causing a type error when passing these props.
  onProcedureChange?: (params: ProcedureChangeParams) => Promise<void>;
  // Only used in VERSION_UPDATE mode (redeclared as required there). Declared here
  // because legacyRunModal's type signature drops fields that aren't shared across all
  // variants of the union, causing a type error when passing these props.
  outdatedFlowchartVersionId?: string;
  currentFlowchartVersionId?: string;
  onApplyVersionUpdate?: () => Promise<void>;
  /**
   * When true, only PoW-backed procedures are shown in the procedure dropdown.
   * Used by procedure run plan flows that no longer support legacy procedures.
   */
  excludeLegacyProcedures?: boolean;
}

export enum EditProcedureModalMode {
  VERSION_UPDATE = 'versionUpdate',
  PROCEDURE_CHANGE = 'procedureChange',
}

interface VersionUpdateEditProcedureModalProps extends BaseEditProcedureModalProps {
  mode: EditProcedureModalMode.VERSION_UPDATE;
  onApplyVersionUpdate: () => Promise<void>;
  outdatedFlowchartVersionId: string;
  currentFlowchartVersionId: string;
}

interface ProcedureChangeEditProcedureModalProps extends BaseEditProcedureModalProps {
  mode?: EditProcedureModalMode.PROCEDURE_CHANGE;
  onProcedureChange: (params: ProcedureChangeParams) => Promise<void>;
}

type EditProcedureModalProps = VersionUpdateEditProcedureModalProps | ProcedureChangeEditProcedureModalProps;

export default function EditProcedureModal(props: EditProcedureModalProps): JSX.Element {
  const isVersionUpdateMode = props.mode === EditProcedureModalMode.VERSION_UPDATE;
  const [newProcedureId, setNewProcedureId] = useState<string | null>(null);

  // Fetch the newly selected procedure's info
  const {procedure: newProcedure} = useExperimentProcedure(
    newProcedureId ? {procedureId: newProcedureId} : null,
    props.studyType
  );

  const procedureUIStringLowercase = getProcedureUIString(
    ProceduresStringKeys.PROCEDURE,
    props.procedureType,
    {stringCase: StringCase.LOWER}
  );

  // checks to see if the selected procedure info has been successfully fetched
  const isProcedureReady = newProcedure != null && newProcedureId === newProcedure.id;
  const canSubmit = isVersionUpdateMode || (newProcedureId != null && isProcedureReady);

  const handleUpdate = async (): Promise<void> => {
    if (!canSubmit) {
      return;
    }

    if (props.mode === EditProcedureModalMode.VERSION_UPDATE) {
      await withLoadingScreen(props.onApplyVersionUpdate(), 'Updating template version...');
      props.onRequestClose();
      return;
    }

    if (newProcedure == null || newProcedureId == null) {
      return;
    }

    const {versionId, isPoW} = selectFlowchartVersion(newProcedure);

    await withLoadingScreen(
      props.onProcedureChange({
        procedureId: newProcedureId,
        procedure: newProcedure,
        ...(isPoW
          ? {workflowFlowchartConfigVersionId: versionId}
          : {templateProcedureFlowchartVersionId: versionId}),
      }),
      `Updating ${procedureUIStringLowercase}...`
    );
    props.onRequestClose();
  };

  return (
    <Modal
      title={isVersionUpdateMode ? 'Update template version' : `Update ${procedureUIStringLowercase}`}
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          text: 'Back',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: props.onRequestClose,
        },
        {
          text: 'Update',
          submit: true,
          disabled: !canSubmit,
          onClick: handleUpdate,
        },
      ]}
    >
      <div className={css(styles.bannerContainer)}>
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={
            isVersionUpdateMode
              ? 'Updating clears all planned values if any definitions changed'
              : `Changing the ${procedureUIStringLowercase} for your design will remove values from your current design.`
          }
          supportingInfo={
            isVersionUpdateMode
              ? 'If no parameters, material inputs, or equipment changed, your values carry over'
              : 'Any values you have for parameters, material inputs, or equipment will be removed.'
          }
        />
      </div>

      {props.mode === EditProcedureModalMode.VERSION_UPDATE ? (
        <div className={css(styles.fieldsContainer)}>
          <div className={css(styles.field)}>
            <FieldLabel label="Outdated version" />
            <ProcedureChip
              id={props.procedureId}
              flowchartVersionId={props.outdatedFlowchartVersionId ?? props.flowchartVersionId}
              size={ChipSize.SMALL}
            />
          </div>
          <div className={css(styles.field)}>
            <FieldLabel label="Current version" />
            <ProcedureChip
              id={props.procedureId}
              flowchartVersionId={props.currentFlowchartVersionId ?? props.flowchartVersionId}
              size={ChipSize.SMALL}
            />
          </div>
        </div>
      ) : (
        <div className={css(styles.fieldsContainer)}>
          <div className={css(styles.field)}>
            <FieldLabel label={`Current ${procedureUIStringLowercase}`} />
            <ProcedureChip
              id={props.procedureId}
              flowchartVersionId={props.flowchartVersionId}
              size={ChipSize.SMALL}
            />
          </div>

          <div className={css(styles.field)}>
            <FieldLabel label={`New ${procedureUIStringLowercase}`} />
            <ProcedureDropdown
              isDisabled={false}
              procedureType={props.procedureType}
              selectedProcedureId={newProcedureId}
              onChangeSelectedProcedure={setNewProcedureId}
              onRequestCloseModal={props.onRequestClose}
              procedureFetchPolicy="network-only"
              placeholderText={`Select ${procedureUIStringLowercase}`}
              excludeProcedureId={props.procedureId}
              excludeLegacyProcedures={props.excludeLegacyProcedures}
            />
            {newProcedureId != null && !isProcedureReady && (
              <ProcedureValidationLoadingSpinner procedureType={props.procedureType} />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  bannerContainer: {
    marginBottom: 20,
  },
  fieldsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 20,
  },
  field: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  versionLabelText: {
    fontSize: 13,
    lineHeight: '18px',
  },
});
